import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { Formik, Form } from "formik";
import { Box, Grid, Paper, Skeleton, Typography } from "@mui/material";
import { Uuid } from "../../../../utils/uuid";
import { convertFileToBase64, normalizeError } from "app/utils";
import { actions as AppActions } from "../../../App/Ducks/App.duck";
import { MESSAGE_SEVERITY_ERROR } from "../../../../common/constants";
import ownerService from "../../Services/OwnerService";
import { useBreadcrumb } from "app/components/Breadcrumbs";
import CreateOwnerForm from "../../Forms/CreateOwnerForm";
import { ButtonStyled, PaperInfoStyled } from "./OwnerAddPage.styles";
const OwnerAddPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const intl = useIntl();
  const [ownersTotalCount, setOwnersTotalCount] = useState({});
  const userId = useSelector((state) => state.AppReducer.user.userId);
  const formikInitialValues = useMemo(
    () => ({
      ownerId: Uuid.newV4(),
      ownerName: "",
      ownerEmail: "",
      ownerPhone: "",
      contactName: "",
      contactEmail: "",
      contactPhone: "",
      address: "",
      zipCode: "",
      city: "",
      instance: "",
      kvkNumber: "",
      parentId: null,
      subOwners: [],
      logo: ""
    }),
    []
  );
  const formikValidationSchema = useMemo(
    () => Yup.object().shape({
      ownerName: Yup.string().trim().strict().max(255).required(intl.formatMessage({ id: "owners.form.ownerName.required" })),
      ownerEmail: Yup.string().trim().strict().max(255).email(),
      ownerPhone: Yup.string().trim().strict().max(255),
      contactName: Yup.string().trim().strict().max(255).required(intl.formatMessage({ id: "owners.form.contactName.required" })),
      contactEmail: Yup.string().trim().strict().max(255).required(intl.formatMessage({ id: "owners.form.contactEmail.required" })).email(),
      contactPhone: Yup.string().trim().strict().max(255),
      address: Yup.string().trim().strict().required(intl.formatMessage({ id: "owners.form.address.required" })).max(255),
      zipCode: Yup.string().trim().strict().max(255),
      city: Yup.string().trim().strict().max(255),
      instance: Yup.string(),
      kvkNumber: Yup.string().when("instance", {
        is: (instance) => instance && instance.trim().length > 0,
        then: (schema) => schema.trim().strict().required(intl.formatMessage({ id: "owners.form.kvkNumber.required" })).test("is-number", intl.formatMessage({ id: "form.validNumber" }), (value) => !isNaN(Number(value))),
        otherwise: (schema) => schema.trim().strict().test("is-number", intl.formatMessage({ id: "form.validNumber" }), function(value) {
          if (!value) return true;
          return !isNaN(Number(value));
        })
      })
    }),
    [intl]
  );
  const saveOwner = useCallback(async (values) => {
    const valuesToSubmit = { ...values };
    if (values.logo && typeof values.logo !== "string") {
      valuesToSubmit.logo = await convertFileToBase64(values.logo);
    }
    return ownerService.createOwner(valuesToSubmit);
  }, []);
  const loadTotalCount = useCallback(async () => {
    try {
      const response = await ownerService.getOwnersTotalCount(userId);
      setOwnersTotalCount(response.data);
    } catch (error) {
      const severity = MESSAGE_SEVERITY_ERROR;
      const details = {
        method: "getOwnersTotalCount",
        severity
      };
      dispatch(AppActions.displayMessage(details));
    }
  }, [dispatch, userId]);
  useEffect(() => {
    loadTotalCount();
  }, [loadTotalCount]);
  const handleCancel = useCallback(() => {
    navigate("../");
  }, [navigate]);
  const handleCreateOwner = useCallback(
    async (values) => {
      try {
        await saveOwner(values);
        navigate("../");
      } catch (err) {
        const errors = normalizeError(err);
        const severity = MESSAGE_SEVERITY_ERROR;
        const details = {
          method: "createOwner",
          severity,
          message: errors.message
        };
        dispatch(AppActions.displayMessage(details));
      }
    },
    [dispatch, navigate, saveOwner]
  );
  const title = useMemo(() => {
    return /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(Typography, { variant: "h5" }, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "owners.add" })), /* @__PURE__ */ React.createElement(Typography, { variant: "subtitle2", sx: { textTransform: "uppercase" } }, typeof ownersTotalCount?.total !== "undefined" ? /* @__PURE__ */ React.createElement(FormattedMessage, { id: "owners.totalCount", values: { totalCount: ownersTotalCount.total } }) : /* @__PURE__ */ React.createElement(Skeleton, { width: 200 })));
  }, [ownersTotalCount]);
  useBreadcrumb("owners.add", {
    hideTrail: true,
    titleElement: title
  });
  return /* @__PURE__ */ React.createElement(Paper, { elevation: 0, square: true }, /* @__PURE__ */ React.createElement(
    Formik,
    {
      initialValues: formikInitialValues,
      validationSchema: formikValidationSchema,
      onSubmit: handleCreateOwner
    },
    () => /* @__PURE__ */ React.createElement(Form, null, /* @__PURE__ */ React.createElement(Grid, { container: true, spacing: 3, direction: "column", xs: 6 }, /* @__PURE__ */ React.createElement(Grid, { item: true, xs: 12 }, /* @__PURE__ */ React.createElement(PaperInfoStyled, null, /* @__PURE__ */ React.createElement(CreateOwnerForm, { onLogoChange: () => {
    } }))), /* @__PURE__ */ React.createElement(Grid, { item: true, xs: 12 }, /* @__PURE__ */ React.createElement(Box, { textAlign: "right" }, /* @__PURE__ */ React.createElement(ButtonStyled, { onClick: handleCancel, variant: "outlined" }, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "button.cancel" })), /* @__PURE__ */ React.createElement(ButtonStyled, { type: "submit", variant: "contained" }, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "owners.add.createNewOwner" }))))))
  ));
};
export default OwnerAddPage;
